import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ForumCategory, CreateForumPostData } from '../types';
import { apiClient } from '../services/apiClient';
import { LoadingSpinner } from './LoadingSpinner';
import { useAuth } from '../hooks/useAuth';
import { useEmailVerificationGuard } from '../hooks/useEmailVerification';
import { EmailVerificationOverlay } from './EmailVerificationOverlay';
import { RichTextEditor, SafeHtmlViewer } from './RichTextEditor';
import DOMPurify from 'dompurify';

export function CreateForumPost() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { checkEmailVerification, requiresVerification } = useEmailVerificationGuard();
  
  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    content_html: '',
    category_id: searchParams.get('category') || ''
  });
  const [showPreview, setShowPreview] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showVerificationOverlay, setShowVerificationOverlay] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchCategories();
  }, [user, navigate]);

  // Update overlay state when verification status changes
  useEffect(() => {
    setShowVerificationOverlay(requiresVerification);
  }, [requiresVerification]);

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await apiClient.get('/forum?path=categories');
      
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleContentChange = (content: string) => {
    setFormData(prev => ({ ...prev, content_html: content }));
    // Extract plain text for basic validation
    const plainText = content.replace(/<[^>]*>/g, '').trim();
    setFormData(prev => ({ ...prev, content: plainText }));
    
    if (errors.content && plainText.length >= 10) {
      setErrors(prev => ({ ...prev, content: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Ο τίτλος είναι υποχρεωτικός';
    } else if (formData.title.length > 200) {
      newErrors.title = 'Ο τίτλος δεν μπορεί να υπερβαίνει τους 200 χαρακτήρες';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Το περιεχόμενο είναι υποχρεωτικό';
    } else if (formData.content.length < 10) {
      newErrors.content = 'Το περιεχόμενο πρέπει να έχει τουλάχιστον 10 χαρακτήρες';
    }

    if (!formData.category_id) {
      newErrors.category_id = 'Η επιλογή κατηγορίας είναι υποχρεωτική';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    // Email verification is handled by the overlay blocking access

    try {
      setSubmitting(true);
      
      // Sanitize HTML content before submitting
      const sanitizedHtml = DOMPurify.sanitize(formData.content_html, {
        ALLOWED_TAGS: [
          'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
          'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          'ul', 'ol', 'li',
          'blockquote', 'pre', 'code',
          'a', 'img',
          'table', 'thead', 'tbody', 'tr', 'th', 'td',
          'sup', 'sub', 'span'
        ],
        ALLOWED_ATTR: [
          'href', 'src', 'alt', 'title', 'width', 'height',
          'class', 'style', 'target', 'rel'
        ],
        ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
      });
      
      const postData: CreateForumPostData = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        content_html: sanitizedHtml,
        category_id: formData.category_id
      };

      const response = await apiClient.post('/forum?path=posts', postData);

      if (response.data.success) {
        const newPost = response.data.data;
        navigate(`/forum/post/${newPost.id}`);
      } else {
        alert(response.data.message);
      }
    } catch (err: any) {
      console.error('Error creating post:', err);
      if (err.response?.data?.message) {
        alert(err.response.data.message);
      } else {
        alert('Σφάλμα κατά τη δημιουργία της δημοσίευσης');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getSelectedCategoryName = () => {
    const selectedCategory = categories.find(cat => cat.id === formData.category_id);
    return selectedCategory?.name;
  };

  const canUserPostInCategory = (category: ForumCategory) => {
    // Check if category is "Ανακοινώσεις" or has posting restrictions
    if (category.name === 'Ανακοινώσεις' || category.posting_restrictions === 'moderators_only') {
      return user?.role === 'moderator' || user?.role === 'admin';
    }
    return true;
  };

  const getSelectedCategory = () => {
    return categories.find(cat => cat.id === formData.category_id);
  };

  if (!user) {
    return null; // Will redirect to login
  }

  return (
    <div className="relative">
      <div className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${showVerificationOverlay ? "opacity-30 pointer-events-none" : ""}`}>
      {/* Breadcrumb */}
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link to="/forum" className="text-gray-700 hover:text-blue-600">
              Φόρουμ
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
              </svg>
              <span className="ml-1 md:ml-2 text-gray-500">Νέα Δημοσίευση</span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Δημιουργία Νέας Δημοσίευσης</h1>
        <p className="mt-2 text-gray-600">
          Μοιραστείτε τις απόψεις και τις ερωτήσεις σας με την κοινότητα
        </p>
      </div>

      {/* Form */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <form onSubmit={handleSubmit}>
          {/* Form Header */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Στοιχεία Δημοσίευσης</h2>
          </div>

          <div className="px-6 py-6 space-y-6">
            {/* Category Selection */}
            <div>
              <label htmlFor="category_id" className="block text-sm font-medium text-gray-700 mb-2">
                Κατηγορία *
              </label>
              {categoriesLoading ? (
                <div className="flex items-center space-x-2">
                  <LoadingSpinner />
                  <span className="text-sm text-gray-500">Φόρτωση κατηγοριών...</span>
                </div>
              ) : (
                <select
                  id="category_id"
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.category_id ? 'border-red-300' : 'border-gray-300'
                  }`}
                  disabled={submitting}
                >
                  <option value="">Επιλέξτε κατηγορία</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              )}
              {errors.category_id && (
                <p className="mt-1 text-sm text-red-600">{errors.category_id}</p>
              )}
              {formData.category_id && (
                <div className="mt-1">
                  <p className="text-sm text-gray-500">
                    Επιλεγμένη κατηγορία: <span className="font-medium">{getSelectedCategoryName()}</span>
                  </p>
                  {getSelectedCategory() && !canUserPostInCategory(getSelectedCategory()!) && (
                    <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-yellow-800">
                            <strong>Περιορισμένη πρόσβαση:</strong> Μόνο συντονιστές και διαχειριστές μπορούν να δημοσιεύουν σε αυτή την κατηγορία.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                Τίτλος *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Γράψτε έναν περιγραφικό τίτλο για τη δημοσίευσή σας"
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.title ? 'border-red-300' : 'border-gray-300'
                }`}
                maxLength={200}
                disabled={submitting}
              />
              <div className="mt-1 flex justify-between text-sm">
                {errors.title ? (
                  <p className="text-red-600">{errors.title}</p>
                ) : (
                  <p className="text-gray-500">Ένας καλός τίτλος βοηθά τους άλλους να κατανοήσουν το θέμα σας</p>
                )}
                <p className="text-gray-400">{formData.title.length}/200</p>
              </div>
            </div>

            {/* Content */}
            <div>
              <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
                Περιεχόμενο *
              </label>
              {!showPreview ? (
                <>
                  <RichTextEditor
                    value={formData.content_html}
                    onChange={handleContentChange}
                    placeholder="Αναπτύξτε το θέμα σας εδώ. Μπορείτε να περιγράψετε το πρόβλημα, να θέσετε ερωτήματα ή να μοιραστείτε τις εμπειρίες σας..."
                    height={400}
                    disabled={submitting}
                    mode="full"
                  />
                  {errors.content && (
                    <p className="mt-1 text-sm text-red-600">{errors.content}</p>
                  )}
                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-sm text-gray-500">
                      Γράψτε με σαφήνεια και λεπτομέρεια για να λάβετε καλύτερες απαντήσεις
                    </p>
                    <div className="text-sm text-gray-500">
                      <span className="font-medium">Συμβουλή:</span> Χρησιμοποιήστε Ctrl+Shift+M για μm, Ctrl+Shift+X για χ
                    </div>
                  </div>
                </>
              ) : (
                <div className="border border-gray-300 rounded-md p-4 min-h-[400px] bg-gray-50">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Προεπισκόπηση:</h3>
                  <div className="bg-white p-4 rounded border border-gray-200">
                    <SafeHtmlViewer html={formData.content_html || '<p class="text-gray-400">Δεν έχετε γράψει περιεχόμενο ακόμα</p>'} />
                  </div>
                </div>
              )}
            </div>

            {/* Guidelines */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-900 mb-2">Οδηγίες για καλή δημοσίευση:</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Χρησιμοποιήστε περιγραφικό τίτλο</li>
                <li>• Αναπτύξτε το θέμα σας με σαφήνεια</li>
                <li>• Συμπεριλάβετε σχετικές λεπτομέρειες</li>
                <li>• Τηρήστε τους κανόνες της κοινότητας</li>
                <li>• Σεβαστείτε τους συναδέλφους σας</li>
              </ul>
            </div>
          </div>

          {/* Form Actions */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <Link
              to="/forum"
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Ακύρωση
            </Link>

            <div className="flex items-center space-x-4">
              {/* Preview Button */}
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={submitting}
              >
                {showPreview ? 'Επεξεργασία' : 'Προεπισκόπηση'}
              </button>

              <button
                type="submit"
                disabled={
                  submitting || 
                  !formData.title.trim() || 
                  !formData.content.trim() || 
                  !formData.category_id ||
                  (getSelectedCategory() && !canUserPostInCategory(getSelectedCategory()!))
                }
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? (
                  <div className="flex items-center space-x-2">
                    <LoadingSpinner />
                    <span>Δημοσίευση...</span>
                  </div>
                ) : (
                  'Δημοσίευση'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      </div>

      {/* Email Verification Overlay */}
      {showVerificationOverlay && (
        <EmailVerificationOverlay
          actionName="δημοσιεύσετε στο φόρουμ"
        />
      )}
    </div>
  );
}